// src/pages/Reports/ReportMissingSubjects.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import {
  useAllSubjects,
  useStudentsFiltered,
  useEnrollmentsByStudents,
  buildMissingMap,
  useAllEnrollmentYears, // ← NEW
} from "../../hooks/useReportMissingSubjects";

/** Small chip used in the header, styled to match your ReportIntake look */
function Chip({ children, tone = "indigo" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs
      ${tone === "indigo" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : ""}
      ${tone === "sky" ? "border-sky-200 bg-sky-50 text-sky-700" : ""}
      ${tone === "slate" ? "border-slate-200 bg-slate-50 text-slate-700" : ""}`}
    >
      {children}
    </span>
  );
}

const PAYMENT_TYPES = ["All", "Full", "Individual"];

export default function ReportMissingSubjects() {
  // Filters
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedPaymentType, setSelectedPaymentType] = useState("All");

  // Subjects
  const {
    data: subjects = [],
    isLoading: isLoadingSubjects,
    isError: isErrorSubjects,
    error: subjectsError,
  } = useAllSubjects();

  // All years (for dropdown)
  const { data: allYears = [] } = useAllEnrollmentYears();
  const yearOptions = useMemo(() => ["All", ...allYears], [allYears]);

  // Validate selectedYear whenever options change
  useEffect(() => {
    if (selectedYear !== "All" && !allYears.includes(selectedYear)) {
      setSelectedYear("All");
    }
  }, [allYears, selectedYear]);

  // Students filtered
  const {
    data: students = [],
    isLoading: isLoadingStudents,
    isError: isErrorStudents,
    error: studentsError,
  } = useStudentsFiltered(selectedYear, selectedPaymentType);

  // Enrollments (only for the filtered students to reduce payload)
  const studentIds = useMemo(() => students.map((s) => s.id), [students]);
  const {
    data: enrollments = [],
    isLoading: isLoadingEnrollments,
    isError: isErrorEnrollments,
    error: enrollmentsError,
  } = useEnrollmentsByStudents(studentIds);

  // Compute per-student missing subjects (array of codes)
  const subjectsById = useMemo(() => {
    const m = new Map();
    subjects.forEach((s) => m.set(s.id, s));
    return m;
  }, [subjects]);

  const subjectCodes = useMemo(() => subjects.map((s) => s.code), [subjects]);

  const missingByStudentId = useMemo(() => {
    return buildMissingMap({
      students,
      enrollments,
      subjects,
      subjectsById,
    });
  }, [students, enrollments, subjects, subjectsById]);

  // CSV helpers
  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const todayKL = useMemo(() => {
    const y = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const m = new Intl.DateTimeFormat("en-GB", {
      month: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const d = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    return `${y}${m}${d}`; // YYYYMMDD
  }, []);

  const handleExportCsv = () => {
    const headers = [
      "No.",
      "Student Name",
      "Contact Number",
      "Email Address",
      "Enrollment Year",
      "Payment Type",
      "Subjects Yet To Enroll",
    ];
    const lines = [headers.map(csvEscape).join(",")];

    students.forEach((stu, idx) => {
      const missing = missingByStudentId.get(stu.id) ?? [];
      const row = [
        idx + 1,
        stu.full_name,
        stu.contact_no ?? "",
        stu.email ?? "",
        stu.enrollment_year ?? "",
        stu.payment_type ?? "",
        missing.join("; "), // semicolon for clarity
      ];
      lines.push(row.map(csvEscape).join(","));
    });

    const filename = `excel_rism_missing_subjects_${selectedYear}_${selectedPaymentType}_${todayKL}.csv`;
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportEnabled =
    students.length > 0 &&
    !isLoadingSubjects &&
    !isLoadingStudents &&
    !isLoadingEnrollments &&
    !isErrorSubjects &&
    !isErrorStudents &&
    !isErrorEnrollments;

  return (
    <div className="p-2 md:p-2">
      {/* Header card with gradient accent — styled like your Intake report */}
      <div className="mb-4">
        <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <div className="h-2 w-full bg-gradient-to-r from-indigo-500 via-sky-400 to-cyan-400" />
          <div className="bg-white p-4 md:p-5">
            <h1 className="text-xl font-semibold text-slate-800">
              Students • Subjects Yet To Enroll
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Filter by Enrollment Year and Enrollment Type. Click a student to view details.
            </p>

            {/* Live filter chips */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip tone="indigo">
                Year: {selectedYear === "All" ? "All Years" : selectedYear}
              </Chip>
              <Chip tone="sky">
                Enrollment Type: {selectedPaymentType === "All" ? "All" : selectedPaymentType}
              </Chip>
            </div>

            {/* Filters + Export */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[14rem,14rem,1fr] md:items-end">
              {/* Year */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Enrollment Year
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={isLoadingStudents || isErrorStudents}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y === "All" ? "All Years" : y}
                    </option>
                  ))}
                </select>
                {isErrorStudents && (
                  <p className="mt-1 text-xs text-red-600">
                    Failed to load students: {studentsError?.message || "Unknown error"}
                  </p>
                )}
              </div>

              {/* Payment Type */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Payment Type
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedPaymentType}
                  onChange={(e) => setSelectedPaymentType(e.target.value)}
                >
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Export */}
              <div className="flex justify-start md:justify-end">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={!exportEnabled}
                  title="Export what’s on screen"
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-sm
                    ${
                      exportEnabled
                        ? "bg-sky-600 text-white hover:bg-sky-700"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="p-3 md:p-4">
          {isLoadingSubjects || isLoadingStudents || isLoadingEnrollments ? (
            <div className="text-xs text-slate-500">Loading…</div>
          ) : isErrorSubjects ? (
            <div className="text-xs text-red-600">
              Failed to load subjects: {subjectsError?.message || "Unknown error"}
            </div>
          ) : isErrorStudents ? (
            <div className="text-xs text-red-600">
              Failed to load students: {studentsError?.message || "Unknown error"}
            </div>
          ) : isErrorEnrollments ? (
            <div className="text-xs text-red-600">
              Failed to load enrollments: {enrollmentsError?.message || "Unknown error"}
            </div>
          ) : students.length === 0 ? (
            <div className="text-xs text-slate-500">No matching students.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-slate-600 border-b">
                    <th className="py-2 pr-2">No.</th>
                    <th className="py-2 pr-2">Student Name</th>
                    <th className="py-2 pr-2">Contact Number</th>
                    <th className="py-2 pr-2">Email Address</th>
                    <th className="py-2 pr-2">Enrollment Year</th>
                    <th className="py-2 pr-2">List of Subjects Yet To Enroll</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((stu, idx) => {
                    const missing = missingByStudentId.get(stu.id) ?? [];
                    return (
                      <tr
                        key={stu.id}
                        className={`border-b last:border-0 ${
                          idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                        } hover:bg-sky-50/60`}
                      >
                        <td className="py-2 pr-2">{idx + 1}</td>
                        <td className="py-2 pr-2">
                          <Link
                            to={{ pathname: `/students/${stu.id}`, search: "?from=report" }}
                            state={{ from: "report" }}
                            className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                          >
                            {stu.full_name}
                          </Link>
                        </td>
                        <td className="py-2 pr-2">{stu.contact_no ?? "-"}</td>
                        <td className="py-2 pr-2">{stu.email ?? "-"}</td>
                        <td className="py-2 pr-2">{stu.enrollment_year ?? "-"}</td>
                        <td className="py-2 pr-2">
                          {missing.length ? (
                            <div className="flex flex-wrap gap-1">
                              {missing.map((code) => (
                                <span
                                  key={code}
                                  className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700 border border-amber-100 text-[11px]"
                                >
                                  {code}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-emerald-700 text-xs">All enrolled</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-2 text-[11px] text-slate-500">
                Showing {students.length} students • Year:{" "}
                {selectedYear === "All" ? "All" : selectedYear} • Payment Type:{" "}
                {selectedPaymentType}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
